// runtime-match-flow.js — live scoring glue between pure engine & UI/broadcast layer
import { get } from 'svelte/store';
import { buzz } from './haptics.js';
import { toast } from './ui/dialogs.js';
import {
  currentScreen, activeMatch, modalState, lastBallState, activeLowerThird, peerRole,
  fullScreenBoard, fullScreenBoardStartedAt, inningsEndPending
} from './svelte/store.js';
import { appSettings, persist } from './store.js';
import {
  runRate, currentInnings, recordRuns, recordWicket, isOverComplete, isAwaitingNewBowler,
  isInningsOver, computeResult, currentOverSummary, formatOvers,
  startSecondInnings, phaseBoundaries, isMaidenOver
} from './scoring-logic.js';
import { setAnimationInitializer, triggerAnimation } from './broadcast/overlay-state.js';
import { _makeConfetti, _makeFirework, _makeStumps } from './runtime-anim.js';
import { sfxFour, sfxSix, sfxWicket, sfxMilestone, sfxDot, sfxHorn, sfxCheer, playCommentaryAudio } from './runtime-audio.js';
import { sr } from './runtime-draw-utils.js';
import { teamObj, teamColor, battingTeamLetter } from './runtime-teams.js';
import { consecutiveSixesByStriker, bowlerHatTrickStatus, duckTypeFor } from './match-stats.js';
import { liveTransitions } from './transition-controller.js';
import { go } from './router.js';
import { sendCurrentMatchState } from './sync/state-publisher.js';
import { _cancelEndMatchTimers, setEndMatchTimer1 } from './store-runtime.js';
import { generateBallCommentary } from './commentary-engine.js';
import { checkLowerThirdTrigger } from './lower-third-engine.js';
import { logHighlightEvent } from './runtime-recording.js';

let _ballBusy = false;
let _inningsEndTimer = null;

export function cancelPendingInningsEnd() {
  if (_inningsEndTimer) {
    clearTimeout(_inningsEndTimer);
    _inningsEndTimer = null;
  }
  // Unconditional: the timer may have already fired and self-nulled by the time
  // Undo reaches here. Leaving inningsEndPending stuck at true greys out the
  // whole scoring pad permanently — a hard soft-lock of the app.
  inningsEndPending.set(false);
}

export function canPromptForNewBowler(screen, peerRole) {
  if (peerRole === 'recorder') return false;
  return screen === 'scoring' || screen === 'recording';
}

export function proceedAfterInningsEnd() {
  const m = get(activeMatch);
  if (!m) return;
  if (m.result) {
    go('result');
    return;
  }

  // ── First innings just ended: start the chase ──
  // Guard: also handle the edge case where startSecondInnings was already
  // called (innings.length === 2) but the chase hasn't begun (0 balls).
  // Without this, a double-call race would hit the else branch below and
  // computeResult would declare the 1st-innings team winner (0 < target).
  const chaseNotStarted = (m.innings.length === 2 || m.innings.length === 4) &&
    m.innings[m.innings.length - 1].balls === 0 && !m.innings[m.innings.length - 1].currentStriker;

  const isTargetChallenge = m.mode === 'challenge' && m.challenge && m.challenge.type !== 'super_over';

  if ((m.innings.length === 1 && m.mode !== 'single' && !isTargetChallenge) || m.innings.length === 3 || chaseNotStarted) {
    if (m.innings.length === 1 || m.innings.length === 3) {
      startSecondInnings(m);
    }
    persist();  // Save the 2nd innings immediately to survive reloads

    // Check if we are recording, keep the camera rolling and seamless transition!
    import('./svelte/store.js').catch(err => {
      // Offline / uncached chunk: without this the promise rejects unhandled and
      // the innings transition silently dies, stranding the scorer on a stale screen.
      console.error('[MatchFlow] store chunk load failed, falling back to break screen:', err);
      go('break');
      return null;
    }).then((mod) => {
      if (!mod) return;
      const { isRecording, modalState, currentScreen } = mod;
      if (get(isRecording) && get(currentScreen) === 'recording') {
        // Auto open batsman selection for the new innings without breaking recording
        modalState.set({ type: "selectBatsman" });
        // triggerAnim is this module's own function — calling the local one keeps
        // the SECOND INNINGS banner working (runtime-anim.js has no triggerAnim
        // export, so the old dynamic import resolved to undefined and threw).
        const targetRuns = m.innings[m.innings.length - 2].totalRuns + 1;
        triggerAnim({
          type: "BANNER",
          title: "SECOND INNINGS",
          text: "TARGET: " + targetRuns,
          duration: 5000
        });
      } else {
        go('break');
      }
    });
  } else {
    m.result = computeResult(m);
    persist();
    import('./svelte/store.js').then(({ isRecording }) => {
      if (get(isRecording)) {
        fullScreenBoard.set('winner');
        fullScreenBoardStartedAt.set(Date.now());
        _cancelEndMatchTimers();
        // Burn the winner card into the saved video before stopping. Only leave
        // the recorder screen after MediaRecorder has flushed/finalized; unmounting
        // it earlier tears down the camera and can lose the final recording.
        setEndMatchTimer1(setTimeout(async () => {
          setEndMatchTimer1(null);
          try {
            const module = await import('./runtime-recording.js');
            await module.stopRecordingAndWait();
          } catch (err) {
            console.error('[MatchFlow] recording finalization failed:', err);
          } finally {
            fullScreenBoard.set(null);
            go('result');
          }
        }, 6000));
      } else {
        fullScreenBoard.set(null);
        go('result');
      }
    }).catch(err => {
      console.error('[MatchFlow] store chunk load failed on match end:', err);
      fullScreenBoard.set(null);
      go('result');
    });
  }
}

export function openExtraPicker(kind)  { modalState.set({ type: "extraRuns", payload: { kind } });  }

export function openWicketPicker()     { modalState.set({ type: "wicketWizard", step: "delivery", payload: {} });  }

export function finishWicketWizard() {
  withBallGuard(() => {
    buzz([10, 30, 20]);
    // $modalState can be nulled out from under us by a peer-sync injection or a
    // second tap; an unguarded deref here throws inside the ball guard and
    // freezes the entire scoring pad.
    const mPayload = get(modalState)?.payload;
    if (!mPayload) { modalState.set(null); return; }
    modalState.set(null);
    const inn = currentInnings(get(activeMatch));

    const outBatsmanName = mPayload.outBatsman || inn.currentStriker;
    const outBatsman = inn.batsmen.find(b => b.name === outBatsmanName);
    const bowlerObj = inn.bowlers.find(b => b.name === inn.currentBowler);
    
    recordWicket(get(activeMatch), { 
      type: mPayload.type, 
      fielder: mPayload.fielder, 
      outBatsman: outBatsmanName, 
      runsCompleted: mPayload.runsCompleted,
      runSource: mPayload.runSource,
      crossed: !!mPayload.crossed,
      isLegal: mPayload.isLegal !== undefined ? mPayload.isLegal : true,
      extraType: mPayload.extraType || null,
      extraRuns: mPayload.extraRuns || 0
    });
    
    triggerAnim({ type: "W", text: "OUT!", color: "#dc2626", duration: 2500 });
    logHighlightEvent("Wicket");
    sfxWicket();
    const commText = generateBallCommentary({ type: 'wicket', wicketType: mPayload.type }, {
      strikerName: outBatsmanName,
      bowlerName: bowlerObj ? bowlerObj.name : (inn ? inn.currentBowler : ''),
      fielderName: mPayload.fielder
    });
    if (commText) playCommentaryAudio(commText.audio, commText.text);
    lastBallState.set({ text: "🔴 WICKET!", color: "#dc2626", commentary: commText, ballType: 'wicket', ts: Date.now() });
    
    if (outBatsman) {
      triggerAnim({
        type: "WICKET_STATS",
        duration: 5000,
        stats: {
          name: outBatsman.name,
          runs: outBatsman.runs,
          balls: outBatsman.balls,
          fours: outBatsman.fours,
          sixes: outBatsman.sixes,
          sr: sr(outBatsman.runs, outBatsman.balls),
          dismissal: outBatsman.outType || mPayload.type,
          bowler: bowlerObj ? bowlerObj.name : ""
        }
      });
    }

    const duck = duckTypeFor(outBatsman, mPayload.type);
    if (duck === "golden") {
      triggerAnim({ type: "DUCK", text: "🦆 GOLDEN DUCK!", color: "#fbbf24", subText: outBatsman.name + " — out on 1st ball", duration: 4500 });
    } else if (duck === "diamond") {
      triggerAnim({ type: "DUCK", text: "💎🦆 DIAMOND DUCK!", color: "#60a5fa", subText: outBatsman.name + " — run out without facing", duration: 4500 });
    } else if (duck === "regular") {
      triggerAnim({ type: "DUCK", text: "🦆 DUCK!", color: "#a3a3a3", subText: outBatsman.name + " — out for 0 (" + outBatsman.balls + " balls)", duration: 4000 });
    }

    if (mPayload.type !== "RunOut" && bowlerObj) {
      const status = bowlerHatTrickStatus(currentInnings(get(activeMatch)), bowlerObj.name);
      if (status === "achieved") {
        triggerAnim({ type: "HAT_TRICK", text: "HAT-TRICK!! 🎯", color: "#dc2626", subText: bowlerObj.name, duration: 6000 });
        sfxHorn();
        sfxCheer();
      } else if (status === "pending") {
        triggerAnim({ type: "HAT_TRICK_BALL", text: "⚠ HAT-TRICK BALL NEXT! ⚠", color: "#f59e0b", subText: "Bowler: " + bowlerObj.name, duration: 5000 });
      }
    }

    afterBall();
  });
}

export function autoOpenSelectionsIfNeeded() {
  if (!get(activeMatch)) return;
  if (get(peerRole) === "recorder" || get(activeMatch)._remoteControlled) return;
  const inn = currentInnings(get(activeMatch));
  if (!inn) return;
  if (get(modalState)) return;
  const needsEndOverBowler = !isInningsOver(get(activeMatch)) &&
    isAwaitingNewBowler(get(activeMatch)) &&
    canPromptForNewBowler(get(currentScreen), get(peerRole));
  if (!inn.currentStriker || !inn.currentNonStriker) {
    if (inn.currentStriker || inn.currentNonStriker) {
      const avail = availableBatsmen(inn);
      if (avail.length === 0) {
        if (isLastManStanding(get(activeMatch), inn)) { enterLastManMode(inn); return; }
        proceedAfterInningsEnd(); return;
      }
      modalState.set({ type: "selectBatsman" });
    } else {
      const avail = availableBatsmen(inn);
      if (avail.length === 0) { proceedAfterInningsEnd(); return; }
      modalState.set({ type: "selectBatsman" });
    }
  } else if (needsEndOverBowler) {
    modalState.set({ type: "selectBowler", payload: { endOver: true } });
  } else if (!inn.currentBowler) {
    modalState.set({ type: "selectBowler", payload: {} });
  }
}

export function withBallGuard(fn) {
  if (_ballBusy) {
    // Never drop a tap silently — the scorer would believe the ball was
    // recorded and the app score would drift from the real ground score.
    buzz([40]);
    toast("Too fast — that tap was not recorded. Tap again.", "warning", 1800);
    return;
  }
  _ballBusy = true;
  try { fn(); }
  finally { setTimeout(() => { _ballBusy = false; }, 120); }
}

export function _initAnimParticles(a) {
  if (appSettings.animParticlesEnabled === false) return;
  const cx = 640, cy = 260; 
  if (a.type === "4" && !a._confetti) {
    a._confetti = _makeConfetti(40, cx, cy);
    a._shockwave = { r: 0 };
  }
  if (a.type === "6" && !a._firework) {
    a._firework = _makeFirework(50, cx, cy);
    a._confetti = _makeConfetti(30, cx, cy);
    a._shockwave = { r: 0 };
    a._flash = true;
  }
  if (a.type === "W" && !a._stumps) {
    a._stumps = _makeStumps(cx, cy + 60);
    a._shake = { intensity: 8 };
  }
  if (a.type === "MILESTONE" && !a._confetti) {
    a._confetti = _makeConfetti(60, cx, cy);
    a._shockwave = { r: 0 };
  }
}

setAnimationInitializer(_initAnimParticles);

export function triggerAnim(obj) {
  if (appSettings.animTextBannersEnabled === false) {
    const banners = ["BANNER", "MILESTONE", "DUCK", "HAT_TRICK", "HAT_TRICK_BALL", "STREAK", "OVER_SUMMARY", "NEW_BATTER", "NEW_BOWLER", "WD", "NB"];
    if (banners.includes(obj.type)) return;
  }
  if (appSettings.eventAnimationsEnabled === false) return;
  triggerAnimation(obj);
}

export function doRecord(kind, runs) {
  withBallGuard(() => {
    if (kind === "runs") {
      const inn = currentInnings(get(activeMatch));
      const striker = inn.batsmen.find(b => b.name === inn.currentStriker);
      const runsBefore = striker ? striker.runs : 0;
      
      recordRuns(get(activeMatch), runs);
      
      const runsAfter = runsBefore + runs;
      const commText = generateBallCommentary({ runs, type: 'runs' }, {
        strikerName: inn.currentStriker,
        bowlerName: inn.currentBowler,
        strikerRunsAfter: runsAfter
      });
      if (commText) playCommentaryAudio(commText.audio, commText.text);
      
      if (runsAfter >= 100 && runsBefore < 100) {
        triggerAnim({
          type: "MILESTONE", kind: "CENTURY", text: "CENTURY!", color: "#fbbf24",
          name: striker ? striker.name : "", runs: striker ? striker.runs : runsAfter,
          balls: striker ? striker.balls : 0, fours: striker ? striker.fours : 0, sixes: striker ? striker.sixes : 0,
          teamColor: teamColor(teamObj(battingTeamLetter(inn)).name),
          duration: 5500
        });
        sfxMilestone();
        sfxCheer();
        lastBallState.set({ text: "💯 CENTURY!", color: "#fbbf24", commentary: commText, ballType: 'six', ts: Date.now() });
      } else if (runsAfter >= 50 && runsBefore < 50) {
        triggerAnim({
          type: "MILESTONE", kind: "FIFTY", text: "FIFTY!", color: "#10b981",
          name: striker ? striker.name : "", runs: striker ? striker.runs : runsAfter,
          balls: striker ? striker.balls : 0, fours: striker ? striker.fours : 0, sixes: striker ? striker.sixes : 0,
          teamColor: teamColor(teamObj(battingTeamLetter(inn)).name),
          duration: 5000
        });
        sfxMilestone();
        lastBallState.set({ text: "🔥 FIFTY!", color: "#10b981", commentary: commText, ballType: 'four', ts: Date.now() });
      } else if (runs === 4) {
        logHighlightEvent("FOUR");
        triggerAnim({ type: "4", text: "FOUR!", color: "#ea580c", duration: 2500 });
        sfxFour();
        lastBallState.set({ text: "4️⃣ FOUR!", color: "#ea580c", commentary: commText, ballType: 'four', ts: Date.now() });
      } else if (runs === 6) {
        logHighlightEvent("SIX");
        triggerAnim({ type: "6", text: "SIX!", color: "#6d28d9", duration: 2500 });
        sfxSix();
        lastBallState.set({ text: "6️⃣ MAXIMUM!", color: "#6d28d9", commentary: commText, ballType: 'six', ts: Date.now() });
        const innAfter = currentInnings(get(activeMatch));
        const sixStreak = consecutiveSixesByStriker(innAfter, innAfter.currentStriker);
        if (sixStreak === 3) {
          triggerAnim({ type: "STREAK", text: "HAT-TRICK OF SIXES! 🔥🔥🔥", color: "#f59e0b", duration: 4000 });
          sfxMilestone();
        } else if (sixStreak === 4) {
          triggerAnim({ type: "STREAK", text: "4 SIXES IN A ROW! 💥", color: "#f97316", duration: 4000 });
          sfxMilestone();
        } else if (sixStreak === 5) {
          triggerAnim({ type: "STREAK", text: "5 SIXES STRAIGHT! 🚀", color: "#ef4444", duration: 4500 });
          sfxMilestone();
        } else if (sixStreak >= 6) {
          triggerAnim({ type: "STREAK", text: "6 SIXES — YUVRAJ-CLASS! 👑", color: "#dc2626", duration: 5500 });
          sfxMilestone();
          sfxHorn();
        }
      } else if (runs === 0) {
        sfxDot();
        lastBallState.set({ text: "• DOT", color: "#71717a", commentary: commText, ballType: 'dot', ts: Date.now() });
      } else {
        lastBallState.set({ text: runs + " RUN" + (runs > 1 ? "S" : ""), color: "#3b82f6", commentary: commText, ballType: 'runs', ts: Date.now() });
      }
    }
    afterBall();
  });
}

export function availableBatsmen(inn) {
  const battingTeam = teamObj(battingTeamLetter(inn));
  const taken = new Set([inn.currentStriker, inn.currentNonStriker].filter(Boolean));
  // Law 25.4: a batter who RETIRED HURT is not out and may resume — only true
  // dismissals (and Retired Out) remove a player from the pool.
  const out = new Set(inn.batsmen.filter(b => b.out && !b.retired).map(b => b.name));
  return battingTeam.players.filter(p => !taken.has(p) && !out.has(p));
}

export function isLastManStanding(m, inn) {
  if (!m || !m.lms || !inn) return false;
  const hasStriker = !!inn.currentStriker, hasNonStriker = !!inn.currentNonStriker;
  if (hasStriker === hasNonStriker) return false; 
  return availableBatsmen(inn).length === 0;
}

export function enterLastManMode(inn) {
  if (!inn.currentStriker && inn.currentNonStriker) {
    inn.currentStriker = inn.currentNonStriker;
    inn.currentNonStriker = null;
  }
  if (!inn._lastManAnnounced && inn.currentStriker) {
    inn._lastManAnnounced = true;
    triggerAnim({
      type: "BANNER",
      title: "🛡 LAST MAN STANDS!",
      text: inn.currentStriker.toUpperCase() + " BATS ALONE — STAYS ON STRIKE",
      color1: "#b45309", color2: "#78350f",
      duration: 5000
    });
    sfxHorn();
  }
}

export function _peerSyncState() {
  setTimeout(sendCurrentMatchState, 0);
}

export function maybeTriggerAutoMoments(m, inn, endOfOver) {
  if (appSettings.autoBroadcastMoments === false) return;
  const mil = Math.floor(inn.totalRuns / 50) * 50;
  if (mil >= 50 && mil > (inn._teamMilestoneAnnounced || 0)) {
    inn._teamMilestoneAnnounced = mil;
    const battingTeam = teamObj(battingTeamLetter(inn));
    const title = mil === 50 ? "FIFTY UP! 🏏" : mil === 100 ? "HUNDRED UP! 💯" : mil + " UP! 🚀";
    triggerAnim({
      type: "BANNER", title,
      text: battingTeam.name.toUpperCase() + "  " + inn.totalRuns + "/" + inn.wickets + "  (" + formatOvers(inn.balls) + " ov)",
      color1: teamColor(battingTeam.name), color2: "#111111", duration: 4500
    });
    sfxMilestone();
  }

  if (endOfOver && !inn._ppAnnounced) {
    const { ppEnd } = phaseBoundaries(m.overs);
    if (inn.balls === ppEnd * 6) {
      inn._ppAnnounced = true;
      triggerAnim({
        type: "BANNER", title: "⚡ END OF POWERPLAY",
        text: teamObj(battingTeamLetter(inn)).name.toUpperCase() + "  " + inn.totalRuns + "/" + inn.wickets + "  •  RR " + runRate(inn.totalRuns, inn.balls),
        color1: "#2563eb", color2: "#1e3a8a", duration: 5000
      });
    }
  }

  if (m.innings.length === 2 && !inn._finalOverAnnounced) {
    const ballsLeft = m.overs * 6 - inn.balls;
    if (ballsLeft === 6) {
      inn._finalOverAnnounced = true;
      const need = m.innings[0].totalRuns + 1 - inn.totalRuns;
      if (need > 0) {
        triggerAnim({
          type: "BANNER", title: "🔥 FINAL OVER!",
          text: need + " NEEDED OFF THE LAST 6 BALLS",
          color1: "#dc2626", color2: "#7f1d1d", duration: 5000
        });
        sfxHorn();
      }
    }
  }
}

export function afterBall() {
  persist();
  const m = get(activeMatch);
  const inn = currentInnings(m);

  if (inn && inn.ballLog && inn.ballLog.length > 0) {
    const lastBall = inn.ballLog[inn.ballLog.length - 1];
    const strikerObj = inn.batsmen.find(b => b.name === inn.currentStriker);
    const bowlerObj = inn.bowlers.find(b => b.name === inn.currentBowler);
    const trigger = appSettings.autoBroadcastMoments === false
      ? null
      : checkLowerThirdTrigger(lastBall, { 
          striker: strikerObj, 
          bowler: bowlerObj, 
          inn, 
          match: m,
          enablePlayerCards: appSettings.enablePlayerCards 
        });
    if (trigger) {
      activeLowerThird.set(trigger);
    }
  }

  const slotEmpty = !inn.currentStriker || !inn.currentNonStriker;
  const stableLastMan = !!inn.currentStriker && !inn.currentNonStriker &&
    availableBatsmen(inn).length === 0;
  const wicketFell = slotEmpty && !stableLastMan;
  const endOf = isOverComplete(get(activeMatch));
  const inningsFinished = isInningsOver(get(activeMatch));

  // If the innings finished, hold a short 6s "innings break" window before transitioning
  // so the user can see milestone/victory animations and still hit Undo if needed.
  if (inningsFinished) {
    cancelPendingInningsEnd();
    const pendingId = get(activeMatch)?.id;
    const pendingInn = get(activeMatch)?.innings?.length;
    inningsEndPending.set(true);
    _inningsEndTimer = setTimeout(() => {
      _inningsEndTimer = null;
      inningsEndPending.set(false);
      // Also require the user to still be on a live screen. Without this the
      // transition fires while they are in Settings/Scorecard and desyncs the
      // router, dropping them on a blank/broken setup state.
      const onLiveScreen = ['scoring', 'recording', 'break'].includes(get(currentScreen));
      if (onLiveScreen && get(activeMatch)?.id === pendingId && get(activeMatch).innings.length === pendingInn && isInningsOver(get(activeMatch))) {
        proceedAfterInningsEnd();
      }
    }, 6000);
  }

  if (endOf) {
    const overIdx = Math.floor((inn.balls - 1) / 6);
    const bowlerObj = inn.bowlers.find(b => b.name === inn.currentBowler);
    const summary = currentOverSummary(inn, overIdx);
    if (appSettings.autoBroadcastMoments !== false) triggerAnim({
        type: "OVER_SUMMARY",
        duration: 6000,
        overNumber: Math.floor(inn.balls / 6),
        bowler: bowlerObj ? bowlerObj.name : "",
        bowlerStats: bowlerObj ? `${bowlerObj.wickets}-${bowlerObj.runs} (${formatOvers(bowlerObj.balls)})` : "",
        runs: summary.runs,
        wicketCount: summary.wickets,
        ballSummary: summary.balls
      });
    if (appSettings.autoBroadcastMoments !== false && isMaidenOver(inn, overIdx)) {
      triggerAnim({
        type: "BANNER", title: "🏅 MAIDEN OVER!",
        text: (bowlerObj ? bowlerObj.name.toUpperCase() : "BOWLER") + " — 0 RUNS CONCEDED",
        color1: "#b45309", color2: "#78350f", duration: 5000
      });
      sfxMilestone();
    }
    maybeTriggerAutoMoments(get(activeMatch), inn, true);

    // Over complete, innings continues, no wicket → prompt for the next bowler
    // after the over-summary animation.
    if (!wicketFell && !inningsFinished) {
      const pendingId = get(activeMatch)?.id;
      const pendingInn = get(activeMatch)?.innings?.length;
      liveTransitions.schedule("bowler-prompt", {
        delay: 6500,
        guard: () => !!get(activeMatch) &&
          get(activeMatch).id === pendingId &&
          get(activeMatch).innings.length === pendingInn &&
          isAwaitingNewBowler(get(activeMatch)) &&
          !isInningsOver(get(activeMatch)) &&
          canPromptForNewBowler(get(currentScreen), get(peerRole)) &&
          (!get(fullScreenBoard) || get(fullScreenBoard) === "innings1"),
        run: () => modalState.set({ type: "selectBowler", payload: { endOver: true } })
      });
    }
  }

  // A wicket fell — resolve the empty batting slot. THIS is what keeps the
  // scoring pad unfrozen: without it currentStriker stays null and every tile
  // greys out (the reported "sab ruk gaya" bug).
  if (wicketFell) {
    if (isLastManStanding(get(activeMatch), inn)) {
      enterLastManMode(inn);   // lone survivor moves onto strike, bats alone
      persist();
      _peerSyncState();
      return;
    }
    if (!inningsFinished) {
      modalState.set({ type: "selectBatsman" });  // pick the incoming batter
    }
    _peerSyncState();
    return;
  }

  if (!endOf) {
    maybeTriggerAutoMoments(get(activeMatch), inn, false);
  }
  _peerSyncState();
}
